import { useEffect, useId, useRef, type ReactNode, type InputHTMLAttributes,
  type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { Icon } from '../shell/Icon.tsx';
import styles from './Field.module.css';

/* ==========================================================================
   Field / Input / Select / Textarea / Checkbox.

   A Field owns the label, the hint and the error message, and wires the
   htmlFor / aria-describedby so the control is announced with its label and
   its failure. Controls themselves stay dumb.
   ========================================================================== */

type Size = 'sm' | 'md';

const cx = (a: string, b?: string | undefined) => (b === undefined ? a : `${a} ${b}`);

export function Field({
  label,
  hint,
  error,
  required = false,
  optional = false,
  inline = false,
  htmlFor,
  children,
}: {
  label: string;
  hint?: ReactNode;
  error?: string | null | undefined;
  required?: boolean | undefined;
  /**
   * Marks the field the operator may skip. Settled here rather than per form:
   * it is a lower-case parenthetical in `--text-low`, sharing the eyebrow's
   * line, never a second badge and never a re-worded label.
   */
  optional?: boolean | undefined;
  inline?: boolean | undefined;
  htmlFor?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className={styles.field} data-inline={inline}>
      <label className={styles.label} htmlFor={htmlFor}>
        {label}
        {required && <span className={styles.req} aria-hidden="true">*</span>}
        {optional && <span className={styles.opt}>(optional)</span>}
      </label>
      {children}
      {error != null && error !== '' ? (
        <span className={styles.error} role="alert">{error}</span>
      ) : (
        hint !== undefined && <span className={styles.hint}>{hint}</span>
      )}
    </div>
  );
}

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: Size | undefined;
  mono?: boolean | undefined;
  invalid?: boolean | undefined;
  /**
   * Editing inside a table cell. The control drops its fill and its border
   * until you hover or focus it, so twenty editable rows read as twenty rows
   * rather than as twenty boxes. Never use it outside a dense grid — a form
   * field that does not look like a field is a trap.
   */
  bare?: boolean | undefined;
}

export function Input({
  size = 'md', mono = false, invalid = false, bare = false, className, ...rest
}: InputProps) {
  return (
    <input
      {...rest}
      className={cx(styles.control, className)}
      data-size={size}
      data-mono={mono}
      data-invalid={invalid}
      data-bare={bare}
      aria-invalid={invalid || undefined}
    />
  );
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: Size | undefined;
  mono?: boolean | undefined;
  invalid?: boolean | undefined;
  /** See Input.bare — for a select living inside a dense grid. */
  bare?: boolean | undefined;
}

export function Select({
  size = 'md', mono = false, invalid = false, bare = false, className, children, ...rest
}: SelectProps) {
  return (
    <span className={cx(styles.selectWrap, className)}>
      <select
        {...rest}
        className={styles.control}
        data-size={size}
        data-mono={mono}
        data-invalid={invalid}
        data-bare={bare}
        aria-invalid={invalid || undefined}
      >
        {children}
      </select>
      <span className={styles.caret}><Icon name="chevron-down" size={12} /></span>
    </span>
  );
}

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean | undefined;
}

export function Textarea({ invalid = false, className, ...rest }: TextareaProps) {
  return (
    <textarea
      {...rest}
      className={cx(styles.control, className)}
      data-invalid={invalid}
      aria-invalid={invalid || undefined}
    />
  );
}

/** Filter box. `/` focuses the first one on a surface, by convention. */
export function SearchInput({
  size = 'sm',
  ...rest
}: Omit<InputProps, 'type'>) {
  return (
    <span className={styles.search}>
      <span className={styles.searchIcon}><Icon name="search" size={13} /></span>
      <Input {...rest} type="search" size={size} />
    </span>
  );
}

export function Checkbox({
  label,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { label: ReactNode }) {
  const id = useId();
  return (
    <label className={styles.checkRow} htmlFor={rest.id ?? id}>
      <CheckBox {...rest} id={rest.id ?? id} />
      {label}
    </label>
  );
}

/**
 * The box on its own, for a table's selection column where the label is the
 * row. The native input carries every keyboard and assistive behaviour and sits
 * transparent on top of the face it drives; the mark is a real `Icon`, because
 * a CSS data-URI tick is a second icon set that cannot take `currentColor`.
 */
export function CheckBox({
  className,
  indeterminate = false,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { indeterminate?: boolean }) {
  /* `indeterminate` has no HTML attribute — it exists only on the DOM node —
     so it is set imperatively. It stays a declarative prop here so callers
     never have to reach for a ref to express "some rows selected". */
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current !== null) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <span className={cx(styles.box, className)}>
      <input {...rest} ref={ref} type="checkbox" className={styles.boxInput} />
      <span className={styles.boxFace} aria-hidden="true">
        <span className={styles.tick}><Icon name="check" size={12} /></span>
        <span className={styles.dash}><Icon name="minus" size={12} /></span>
      </span>
    </span>
  );
}
